import React, { useLayoutEffect, useRef } from 'react';

import {
  FlowContextProvider,
  FlowValues,
  StepProps,
  StepWrapper,
  useAutoNavigate,
  useMutation,
  useQuery,
  useReturnToPath,
  useSessionStorage,
  when,
} from '@betterment/js-runtime';
import { getLegacySearchParam } from '@betterment/shared-components';
import _compact from 'lodash/compact';
import _pick from 'lodash/pick';
import { Route, Routes, useLocation, useMatch, useParams, useSearchParams } from 'react-router-dom';

import AccountTypeSelection from './AccountTypeSelection';
import BeginSetup from './BeginSetup';
import BeginVerification from './BeginVerification';
import ConfirmAddresses from './ConfirmAddresses';
import Disclosure from './Disclosure';
import FundAccount from './FundAccount';
import LimitError from './LimitError';
import Occupation from './Occupation';
import SourceOfFundsStep from './SourceOfFundsStep';
import StateAgeLimitEligibilityCheck from './StateAgeLimitEligibilityCheck';
import Success from './Success';

import { useCustomerResponse } from '../Checking/components/CustomerResponseProvider';
import { useBankDocuments } from '../Checking/contexts/BankDocumentsProvider';
import { useUser } from '../Checking/contexts/UserProvider';
import {
  Address,
  AddressParams,
  AddressValidationParams,
  Customer,
  CustomerCreateParams,
  CustomerCreateResponse,
} from '../Checking/data';
import { hasMaxAccounts, isVerified } from '../Checking/models/customer';
import FullPageLoader from '../Shared/components/FullPageLoader';
import { CheckingSignupData, CheckingSignupDataParams } from '../Shared/data';
import { locationAssign } from '../Shared/path-helpers';
import { urlBuilder } from '../Shared/urlBuilder';

export const customerValues: CustomerFlowValues = {
  bankAgreements: true,
  retailAgreements: true,
  residentialAddress: {} as Address,
  mailingAddress: {} as Address,
  nextStep: undefined,
  customer: undefined,
  invitationId: undefined,
  accountId: null,
  accountType: 'individual',
  role: undefined,
  sourceOfFunds: undefined,
  stepTitle: 'Checking signup',
};

export interface CustomerFlowValues extends AddressParams, CustomerCreateParams, Partial<CustomerCreateResponse> {
  customer?: Customer | null;
  accountId: number | null;
  accountType?: 'individual' | 'joint';
  sourceOfFunds?: string;
  role?: string;
  stepTitle: string;
}

export interface AddressFormValues extends AddressParams {
  fullName: string;
}
export type CustomerSignupValues = FlowValues<CustomerFlowValues>;

interface CustomerSignupFlowControllerValues {
  variant?: 'prospect' | 'tenured';
}

const getStepTitle = (accountType: 'joint' | 'individual' | undefined) =>
  accountType === 'joint' ? 'Joint Checking signup' : 'Checking signup';

const CustomerSignupFlowController: React.FC<React.PropsWithChildren<CustomerSignupFlowControllerValues>> = ({
  variant = 'tenured',
}) => {
  const { user } = useUser();
  const { customer } = useCustomerResponse();
  const { data: documents, loading: isLoadingDocs } = useBankDocuments();
  const { data: checkingSignupData, loading: isLoadingCheckingSignupData } = useQuery<CheckingSignupData>(
    urlBuilder.getCheckingSignupData.absolute()
  );
  const [persistCheckingSignupData] = useMutation<void, CheckingSignupDataParams>(
    urlBuilder.getCheckingSignupData.absolute(),
    {
      method: 'put',
    }
  );
  const [validateAddress] = useMutation<{}, AddressValidationParams>(urlBuilder.postValidateAddress());
  const matchA = useMatch('/checking/signups/:id/*');
  const matchB = useMatch('/checking/prospect_signup/identity/*');
  const match = matchA || matchB;
  const navigateRouter = useAutoNavigate();
  const returnToPath = useReturnToPath(urlBuilder.appHome.absolute());

  const [searchParams] = useSearchParams();
  const invitationId = getLegacySearchParam(searchParams, 'invitation_id') ?? undefined;
  const accountType = getLegacySearchParam(searchParams, 'account_type') as 'joint' | 'individual' | undefined;
  // NOTE: `id` is undefined for prospect signup, which is probably a bug
  const { id } = useParams<{ id?: string }>();
  const sessionKey = `${id}-checking-customer-signup`;
  const { values: sessionValues, clearValues } = useSessionStorage<CustomerFlowValues>(
    sessionKey,
    invitationId || accountType ? { invitationId, accountType } : undefined
  );

  const { pathname } = useLocation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    modalOverlayRef.current?.scrollTo(0, 0);
  }, [pathname]);

  const signupInvitationId = invitationId ?? sessionValues?.invitationId;
  const isSecondaryAcceptingInvitation = signupInvitationId !== undefined;

  const isJointSignupFlow = (accountType ?? sessionValues?.accountType) === 'joint';

  const isProspectSignupFlow = variant === 'prospect';
  const isTenuredSignupFlow = variant === 'tenured';

  const residentialAddress = _pick(user?.residentialAddress, ['addressLine1', 'addressLine2', 'city', 'state', 'zip']);
  const mailingAddress = residentialAddress;

  const handleClose = () => {
    clearValues();
    void navigateRouter(urlBuilder.appHome.absolute());
  };

  if ((!documents && isLoadingDocs) || (!checkingSignupData && isLoadingCheckingSignupData)) {
    return <FullPageLoader />;
  }

  if (customer && hasMaxAccounts(customer)) {
    locationAssign(returnToPath);
    return null;
  }

  const isVerifiedCustomerWithAnAccount = customer && isVerified(customer) && customer?.accounts.length > 0;
  const isTenuredCheckingCustomer = isTenuredSignupFlow && isVerifiedCustomerWithAnAccount;

  const shouldShowSourceOfFunds = !isTenuredCheckingCustomer || isSecondaryAcceptingInvitation;

  let stepAfterConfirmAddresses = 'submit';
  if (user?.requiresOccupation) {
    stepAfterConfirmAddresses = 'occupation';
  } else if (shouldShowSourceOfFunds) {
    stepAfterConfirmAddresses = 'source_of_funds';
  }

  const buildAccountTypeSelectionStep = (stepProps: StepProps<FlowValues<CustomerFlowValues>>) => {
    const handleIndividualClick = () => {
      if (isTenuredCheckingCustomer) {
        void navigateRouter(urlBuilder.getAppCheckingAccountCreation.absolute());
      } else {
        stepProps.goToNextStep({ accountType: 'individual', stepTitle: getStepTitle('individual') });
      }
    };

    const handleJointClick = () => {
      if (isProspectSignupFlow && customer && customer.accounts.length === 1) {
        void navigateRouter(urlBuilder.getAppCheckingJointAccounts.absolute({ accountId: customer.accounts[0].id }));
      } else if (isProspectSignupFlow && customer && customer.accounts.length > 1) {
        throw new Error('User with more than one account attempted to go through prospect joint checking flow');
      } else if (isTenuredCheckingCustomer) {
        void navigateRouter(urlBuilder.getAppCheckingJointAccounts.absolute({ createNewAccount: true }));
      } else {
        stepProps.goToNextStep({ accountType: 'joint', stepTitle: getStepTitle('joint') });
      }
    };
    return (
      <AccountTypeSelection
        {...stepProps}
        handleIndividualClick={handleIndividualClick}
        handleJointClick={handleJointClick}
        handleClose={handleClose}
      />
    );
  };

  const initialStepOrder = _compact([
    isTenuredSignupFlow && !isTenuredCheckingCustomer && 'state_age_limit_eligibility_check',
    isTenuredSignupFlow && !signupInvitationId && !isJointSignupFlow ? 'account_type_selection' : 'disclosure',
  ]);

  const onSourceOfFundsSubmit = async (values: CheckingSignupDataParams) => {
    await persistCheckingSignupData(values);
  };

  return (
    <FlowContextProvider
      defaultStep={initialStepOrder[0]}
      returnToPath={returnToPath}
      initialValues={{
        ...customerValues,
        invitationId: signupInvitationId,
        accountType,
        customer,
        residentialAddress,
        mailingAddress,
        stepTitle: getStepTitle(accountType),
      }}
      basePath={match!.pathnameBase}
      routePath=""
    >
      <Routes>
        <Route
          path="/state_age_limit_eligibility_check"
          element={
            <StepWrapper name="state_age_limit_eligibility_check" next={initialStepOrder[1]}>
              <StateAgeLimitEligibilityCheck handleClose={handleClose} />
            </StepWrapper>
          }
        />
        <Route
          path="/account_type_selection"
          element={
            <StepWrapper<CustomerSignupValues>
              name="account_type_selection"
              next={[['disclosure', when(() => isTenuredSignupFlow)], 'fund_account']}
            >
              {stepProps => buildAccountTypeSelectionStep(stepProps)}
            </StepWrapper>
          }
        />
        <Route
          path="/disclosure"
          element={
            <StepWrapper<CustomerSignupValues> name="disclosure">
              {stepProps => <Disclosure {...stepProps} documents={documents!} user={user!} handleClose={handleClose} />}
            </StepWrapper>
          }
        />
        <Route
          path="/confirm_addresses"
          element={
            <StepWrapper<CustomerSignupValues> name="confirm_addresses" next={stepAfterConfirmAddresses}>
              {stepProps => (
                <ConfirmAddresses
                  {...stepProps}
                  user={user!}
                  validateAddress={validateAddress}
                  handleClose={handleClose}
                />
              )}
            </StepWrapper>
          }
        />
        <Route
          path="/occupation"
          element={
            <StepWrapper<CustomerSignupValues> name="occupation">
              {stepProps => (
                <Occupation {...stepProps} checkingSignupData={checkingSignupData!} handleClose={handleClose} />
              )}
            </StepWrapper>
          }
        />
        <Route
          path="/source_of_funds"
          element={
            <StepWrapper<CustomerSignupValues>
              name="source_of_funds"
              previous={user?.requiresOccupation ? 'occupation' : 'confirm_addresses'}
            >
              {stepProps => (
                <SourceOfFundsStep
                  {...stepProps}
                  // This is safe bc we are guarding against the loading state above
                  checkingSignupData={checkingSignupData!}
                  onSubmit={onSourceOfFundsSubmit}
                  handleClose={handleClose}
                />
              )}
            </StepWrapper>
          }
        />
        <Route
          path="/submit"
          element={
            <StepWrapper<CustomerSignupValues>
              name="submit"
              next={[
                ['limit_error', when((_, { values }) => values.nextStep === 'limit_error')],
                ['success', when((_, { values }) => values.nextStep === 'success')],
              ]}
              previous={shouldShowSourceOfFunds ? 'source_of_funds' : 'confirm_addresses'}
            >
              {stepProps => <BeginVerification {...stepProps} handleClose={handleClose} />}
            </StepWrapper>
          }
        />
        <Route
          path="/limit_error"
          element={
            <StepWrapper<CustomerSignupValues> name="limit_error">
              {stepProps => <LimitError {...stepProps} handleClose={handleClose} />}
            </StepWrapper>
          }
        />
        <Route
          path="/success"
          element={
            <StepWrapper<CustomerSignupValues>
              name="success"
              next={[
                ['begin_setup', when(() => isProspectSignupFlow)],
                ['fund_account', when(() => isTenuredSignupFlow)],
              ]}
            >
              {stepProps => <Success {...stepProps} />}
            </StepWrapper>
          }
        />
        <Route
          path="/begin_setup"
          element={
            <StepWrapper<CustomerSignupValues> name="begin_setup" next="account_type_selection">
              {stepProps => <BeginSetup {...stepProps} />}
            </StepWrapper>
          }
        />
        <Route
          path="/fund_account"
          element={
            <StepWrapper<CustomerSignupValues> name="fund_account">
              {stepProps => <FundAccount {...stepProps} user={user!} handleClose={handleClose} />}
            </StepWrapper>
          }
        />
      </Routes>
    </FlowContextProvider>
  );
};

export default CustomerSignupFlowController;
